import { logger } from '@/core/logger';
import * as LocalAuthentication from 'expo-local-authentication';
import { Platform } from 'react-native';

/**
 * Biometric authentication wrapper
 * Provides consistent API for biometric authentication across platforms
 * Follows the wrapper pattern for external libraries
 */
export class BiometricAuth {
    /**
     * Check if biometric authentication is available on the device
     */
    static async isBiometricAvailable(): Promise<boolean> {
        try {
            const compatible = await LocalAuthentication.hasHardwareAsync();
            if (!compatible) {
                logger.debug('BiometricAuth:Hardware not compatible');
                return false;
            }

            const enrolled = await LocalAuthentication.isEnrolledAsync();
            if (!enrolled) {
                logger.debug('BiometricAuth:No biometrics enrolled');
                return false;
            }

            logger.debug('BiometricAuth:Biometric available');
            return true;
        } catch (error) {
            logger.error('BiometricAuth:Failed to check availability', error);
            return false;
        }
    }

    /**
     * Get the type of biometric authentication available
     * Returns: 'fingerprint' | 'facial' | 'iris' | 'none'
     */
    static async getBiometricType(): Promise<LocalAuthentication.AuthenticationType[]> {
        try {
            const types = await LocalAuthentication.supportedAuthenticationTypesAsync();
            logger.debug('BiometricAuth:Supported types', { types });
            return types;
        } catch (error) {
            logger.error('BiometricAuth:Failed to get biometric type', error);
            return [];
        }
    }

    /**
     * Authenticate using biometric
     * @param options - Authentication options
     * @returns Promise<{ success: boolean; error?: string }>
     */
    static async authenticateWithBiometric(options?: {
        promptMessage?: string;
        cancelLabel?: string;
        disableDeviceFallback?: boolean;
    }): Promise<{ success: boolean; error?: string }> {
        try {
            const isAvailable = await this.isBiometricAvailable();
            if (!isAvailable) {
                return {
                    success: false,
                    error: 'Biometric authentication is not available on this device',
                };
            }

            const authOptions: LocalAuthentication.LocalAuthenticationOptions = {
                promptMessage: options?.promptMessage || 'Authenticate to continue',
                cancelLabel: options?.cancelLabel || 'Cancel',
                disableDeviceFallback: options?.disableDeviceFallback ?? false,
            };
            if (Platform.OS === 'ios') {
                authOptions.fallbackLabel = 'Use Passcode';
            }
            const result = await LocalAuthentication.authenticateAsync(authOptions);

            if (result.success) {
                logger.info('BiometricAuth:Authentication successful');
                return { success: true };
            } else {
                const errorMessage = this.getErrorMessage(result.error);
                logger.warn('BiometricAuth:Authentication failed', { error: result.error });
                return {
                    success: false,
                    error: errorMessage,
                };
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Biometric authentication failed';
            logger.error('BiometricAuth:Authentication error', error);
            return {
                success: false,
                error: errorMessage,
            };
        }
    }

    /**
     * Check if hardware supports biometric authentication
     */
    static async hasHardware(): Promise<boolean> {
        try {
            return await LocalAuthentication.hasHardwareAsync();
        } catch (error) {
            logger.error('BiometricAuth:Failed to check hardware', error);
            return false;
        }
    }

    /**
     * Check if biometrics are enrolled on the device
     */
    static async isEnrolled(): Promise<boolean> {
        try {
            return await LocalAuthentication.isEnrolledAsync();
        } catch (error) {
            logger.error('BiometricAuth:Failed to check enrollment', error);
            return false;
        }
    }

    /**
     * Convert authentication error code to user-friendly message
     */
    private static getErrorMessage(error?: string): string {
        if (!error) {
            return 'Authentication cancelled';
        }

        switch (error) {
            case 'user_cancel':
                return 'Authentication cancelled';
            case 'user_fallback':
                return 'Fallback authentication selected';
            case 'system_cancel':
                return 'Authentication cancelled by system';
            case 'passcode_not_set':
                return 'Passcode not set on device';
            case 'fingerprint_not_available':
                return 'Fingerprint not available';
            case 'fingerprint_not_enrolled':
                return 'Fingerprint not enrolled';
            case 'not_available':
                return 'Biometric authentication not available';
            case 'not_configured':
                return 'Biometric authentication not configured';
            case 'not_present':
                return 'Biometric sensor not present';
            default:
                return 'Authentication failed';
        }
    }
}

export const biometricAuth = BiometricAuth;

